import { MapStyleSchema } from './map-style-contract.js';
import { CameraPlatformSchema } from './camera-platform-contract.js';
import { ExportMetadataSchema } from './export-metadata.js';
import { CameraGimbalSchema } from './camera-gimbal-contract.js';
import { assertExportBudget } from './export-range.js';
import { ExportRangeSchema, validExportTiming, EXPORT_RANGE_ERROR } from './export-range-contract.js';
import { PlaceTitleSchema } from './place-title.js';
import { CinematicSequenceSchema, validCameraTrackTiming, CAMERA_TRACK_TIMING_ERROR } from './cinematic-sequence-contract.js';
import { z } from "zod";
import { CameraAngleSchema } from './camera-angle-contract.js';
import { DepthOfFieldSchema } from './depth-of-field-contract.js';
import { enabledDepthOfField } from './depth-of-field.js';
import { RECORDED_RIDE_SOURCES } from "./ride-source.js";
import { TrackAppearanceSchema } from './track-appearance-contract.js';
import { VideoPresentationSchema } from "./presentation-contract.js";
import { presentationSupportsPreset } from "./video-presentation.js";
import { isRecordingDevice, type RecordingDevice } from "./recording-device.js";
import type { RenderOutput } from "./presets.js";
import {
  CAMERA_DISTANCE_IDS,
  ROUTE_LINE_STYLE_IDS,
  MAX_RENDER_DURATION_SECONDS,
  MIN_RENDER_DURATION_SECONDS,
  resolveOutput,
} from "./presets.js";

const RidePointSchema = z
  .object({
    elevationMeters: z.number().finite(),
    latitude: z.number().finite().min(-90).max(90),
    longitude: z.number().finite().min(-180).max(180),
    segmentStart: z.boolean().optional(),
    recordedAt: z.iso.datetime({ offset: true }),
  })
  .strict();

/** Runtime schema for private normalized ride objects read from R2. */
export const NormalizedRideSchema = z
  .object({
    id: z.string().min(1).max(128),
    points: z.array(RidePointSchema).min(2).max(100_000),
    region: z.string().max(160),
    source: z.enum([...RECORDED_RIDE_SOURCES, "synthetic"]),
    sourceFileName: z.string().min(1).max(255).optional(),
    recordingDevice: z.custom<RecordingDevice>(isRecordingDevice, "Invalid recording device.").optional(),
    title: z.string().min(1).max(120),
  })
  .strict()
  .superRefine((ride, context) => {
    let previous = -Infinity;
    for (const [index, point] of ride.points.entries()) {
      const timestamp = Date.parse(point.recordedAt);
      if (timestamp < previous) {
        context.addIssue({
          code: "custom",
          message: "Trace timestamps must not regress.",
          path: ["points", index, "recordedAt"],
        });
        return;
      }
      previous = timestamp;
    }
  });

const RenderOutputSchema = z
  .object({
    placeTitle: PlaceTitleSchema.optional(),
    exportRange: ExportRangeSchema.optional(),
    cinematicSequence: CinematicSequenceSchema.optional(),
    depthOfField: DepthOfFieldSchema.optional().transform(enabledDepthOfField),
    cameraGimbal: CameraGimbalSchema.optional(),
  cameraAngle: CameraAngleSchema.optional(),
    trackAppearance: TrackAppearanceSchema.optional(),
    terrainExaggeration: z.number().min(1).max(3).optional(),
    hidePlaces: z.boolean().optional(),
    unitSystem: z.enum(["metric", "imperial"]).optional(),
    presentation: VideoPresentationSchema.optional(),
    mapStyle: MapStyleSchema.optional(),
    lineStyle: z.enum(ROUTE_LINE_STYLE_IDS).default("pipe"),
    cameraDistance: z.enum(CAMERA_DISTANCE_IDS),
    cameraStyle: CameraPlatformSchema,
    cameraStyleVersion: z.union([z.literal(3), z.literal(4)]),
    durationSeconds: z
      .int()
      .min(MIN_RENDER_DURATION_SECONDS)
      .max(MAX_RENDER_DURATION_SECONDS),
    format: z.literal("mp4"),
    framesPerSecond: z.literal(30),
    height: z.int().positive(),
    preset: z.enum(["landscape-16x9", "social-4x5", "vertical-9x16"]),
    width: z.int().positive(),
  })
  .strict()
  .refine(validExportTiming, EXPORT_RANGE_ERROR)
  .refine(validCameraTrackTiming, CAMERA_TRACK_TIMING_ERROR)
  .superRefine((output, context) => {
    if (output.durationSeconds < MIN_RENDER_DURATION_SECONDS || output.durationSeconds > MAX_RENDER_DURATION_SECONDS) return;
    if (output.presentation && !presentationSupportsPreset(output.presentation, output.preset)) {
      context.addIssue({ code: "custom", message: "This placement does not support the selected aspect ratio.", path: ["presentation"] });
    }
    const expected = resolveOutput(
      output.preset,
      output.durationSeconds,
      output.cameraStyle,
      output.cameraDistance,
      output.lineStyle,
      output.cameraStyleVersion,
    );
    if (
      output.format !== expected.format
      || output.framesPerSecond !== expected.framesPerSecond
      || output.width !== expected.width
      || output.height !== expected.height
      || output.cameraStyleVersion !== expected.cameraStyleVersion
    ) {
      context.addIssue({ code: "custom", message: "Render output does not match its preset." });
    }
  });

/** Runtime schema for queue messages crossing into the isolated renderer. */
export const RenderQueueMessageSchema = z
  .object({
    exportMetadata: ExportMetadataSchema.optional(),
    maxVideoSeconds: z.number().int().min(MIN_RENDER_DURATION_SECONDS)
      .transform(maximum => Math.min(maximum, MAX_RENDER_DURATION_SECONDS)).default(MAX_RENDER_DURATION_SECONDS),
    accountId: z.uuid(),
    enqueuedAt: z.iso.datetime({ offset: true }),
    jobId: z.uuid(),
    normalizedTrackKey: z
      .string()
      .min(1)
      .max(512)
      .regex(/^accounts\/[a-zA-Z0-9_-]+\/rides\/[a-zA-Z0-9_-]+\/normalized\.json$/),
    output: RenderOutputSchema,
    rideId: z.uuid(),
    version: z.literal(1),
  })
  .strict().refine(message => validAuthorizedDuration(message.output, message.maxVideoSeconds), "Video exceeds its authorized duration.");

/** Validated normalized ride type. */
export type NormalizedRide = z.infer<typeof NormalizedRideSchema>;

/** Validated queue-message type with a renderer-owned output contract. */
export type RenderQueueMessage = Omit<z.infer<typeof RenderQueueMessageSchema>, "output"> & {
  readonly output: RenderOutput;
};

function validAuthorizedDuration(output: RenderOutput, maximum: number): boolean {
  try { assertExportBudget(output, maximum); return true; } catch { return false; }
}
